import { TextField } from "@mui/material";
import type { SxProps, Theme } from "@mui/material";
import type { KeyboardEvent, ReactNode } from "react";
import { useEffect, useRef } from "react";
import { NUMERIC_FIELD_SX, ROLL_CELL_SX } from "../fieldStyles";
import { stepNumericValue } from "../numericStep";

interface Props {
	value: string;
	onValueChange: (value: string) => void;
	label?: string;
	disabled?: boolean;
	error?: boolean;
	helperText?: ReactNode;
	fullWidth?: boolean;
	endAdornment?: ReactNode;
	/** Clamp for the arrow keys — typing is still free, validation flags it. */
	min?: number | null;
	max?: number | null;
	/** "cell" = the tighter rolls-table variant with no floating label. */
	density?: "field" | "cell";
	sx?: SxProps<Theme>;
}

// Everything a number can look like halfway through being typed: an empty
// field, a lone minus and a trailing dot all have to survive keystroke by
// keystroke, which is why the field is text and not type="number".
const PARTIAL_NUMBER = /^-?\d*\.?\d*$/;

// The recipe form's numeric input. Text rather than number on purpose: a number
// input exposes no caret, and the caret is what tells the arrow keys which digit
// to step (see numericStep).
export const NumericStepField = ({
	value,
	onValueChange,
	label,
	disabled,
	error,
	helperText,
	fullWidth,
	endAdornment,
	min,
	max,
	density = "field",
	sx,
}: Props) => {
	const inputRef = useRef<HTMLInputElement | null>(null);
	// Where the caret must land once React has re-rendered with the stepped value.
	const pendingCaret = useRef<number | null>(null);

	useEffect(() => {
		const el = inputRef.current;
		if (el && pendingCaret.current != null) {
			el.setSelectionRange(pendingCaret.current, pendingCaret.current);
			pendingCaret.current = null;
		}
	});

	const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
		if (e.key !== "ArrowUp" && e.key !== "ArrowDown") return;
		e.preventDefault();
		const el = e.currentTarget;
		const stepped = stepNumericValue(
			el.value,
			el.selectionStart ?? el.value.length,
			e.key === "ArrowUp" ? 1 : -1,
			{ min, max },
		);
		if (!stepped) return;
		pendingCaret.current = stepped.caret;
		onValueChange(stepped.value);
	};

	return (
		<TextField
			label={label}
			value={value}
			disabled={disabled}
			error={error}
			helperText={helperText}
			fullWidth={fullWidth}
			size="small"
			sx={[
				density === "cell" ? ROLL_CELL_SX : NUMERIC_FIELD_SX,
				...(Array.isArray(sx) ? sx : [sx]),
			]}
			// MUI's own prop, not slotProps.htmlInput.ref — that one would replace
			// the ref InputBase keeps for focus handling instead of adding to it.
			inputRef={inputRef}
			onChange={(e) => {
				if (PARTIAL_NUMBER.test(e.target.value)) onValueChange(e.target.value);
			}}
			slotProps={{
				inputLabel: { shrink: true },
				input: endAdornment ? { endAdornment } : undefined,
				htmlInput: { inputMode: "decimal", onKeyDown: handleKeyDown },
			}}
		/>
	);
};
